import React from 'react';
import cn from 'classnames';
import {t} from '@lingui/core/macro';
import {Trans} from '@lingui/react';

import {CreateGatedButton, ICreateGatedButtonProps, useHasTariffLimit} from 'entities/user';
import PaginationChevronLeftIcon from 'shared/ui/icons/PaginationChevronLeftIcon';
import {HttpMonitorModal} from 'features/http';
import {PortMonitorModal} from 'features/port';
import {DomainMonitorModal} from 'features/domain';
import {ServerModal} from 'features/server';
import MonitorsIcon from 'shared/ui/icons/NavMenu/MonitorsIcon';
import ServersIcon from 'shared/ui/icons/NavMenu/ServersIcon';
import PortsIcon from 'shared/ui/icons/NavMenu/PortsIcon';
import DomainsIcon from 'shared/ui/icons/NavMenu/DomainsIcon';
import {reachGoal, YM_GOALS, YmGoal} from 'shared/lib/analytics';

import styles from './StartCards.module.scss';

type StartCardId = 'site' | 'server' | 'port' | 'domain';

interface IStartCard {
    id: StartCardId,
    icon: React.ReactNode,
    label: string,
    text: string,
    canCreate: boolean,
    exceededType: ICreateGatedButtonProps['exceededType'],
    Modal: ICreateGatedButtonProps['Modal'],
    goals: YmGoal[],
}

const StartCards: React.FC = () => {
    const canCreateSite = useHasTariffLimit('monitor_max_count_limit');
    const canCreateServer = useHasTariffLimit('server_max_count_limit');
    const canCreatePort = useHasTariffLimit('port_monitor_max_count_limit');
    const canCreateDomain = useHasTariffLimit('domain_monitor_max_count_limit');

    const cards: IStartCard[] = [
        {
            id: 'site',
            icon: <MonitorsIcon/>,
            label: t`Добавить сайт`,
            text: t`Доступность страницы, код ответа и время отклика`,
            canCreate: canCreateSite,
            exceededType: undefined,
            Modal: HttpMonitorModal,
            goals: [YM_GOALS.ADD_MONITOR, YM_GOALS.START_ADD_SITE],
        },
        {
            id: 'server',
            icon: <ServersIcon/>,
            label: t`Добавить сервер`,
            text: t`Процессор, память, диск и нагрузка — по данным агента`,
            canCreate: canCreateServer,
            exceededType: 'servers',
            Modal: ServerModal,
            goals: [YM_GOALS.START_ADD_SERVER],
        },
        {
            id: 'port',
            icon: <PortsIcon/>,
            label: t`Добавить порт`,
            text: t`Открыт ли TCP-порт — проверка из нескольких точек`,
            canCreate: canCreatePort,
            exceededType: 'port_monitors',
            Modal: PortMonitorModal,
            goals: [YM_GOALS.START_ADD_PORT],
        },
        {
            id: 'domain',
            icon: <DomainsIcon/>,
            label: t`Добавить домен`,
            text: t`Срок регистрации домена и SSL-сертификата`,
            canCreate: canCreateDomain,
            exceededType: 'domain_monitors',
            Modal: DomainMonitorModal,
            goals: [YM_GOALS.START_ADD_DOMAIN],
        },
    ];

    return (
        <div className={styles.block}>
            <h2 className={styles.title}>
                <Trans id="home.plug.start.title" message="С чего начать"/>
            </h2>
            <ul className={styles.cards}>
                {cards.map(({id, icon, label, text, canCreate, exceededType, Modal, goals}) => (
                    <li className={cn(styles.card, styles[id])} key={id}>
                        <span className={styles.icon}>{icon}</span>
                        <span className={styles.label}>
                            <CreateGatedButton
                                label={label}
                                canCreate={canCreate}
                                exceededType={exceededType}
                                Modal={Modal}
                                variant="transparent"
                                className={styles.cardButton}
                                onClick={() => goals.forEach((goal) => reachGoal(goal))}
                            />
                            <span className={styles.chevron}><PaginationChevronLeftIcon/></span>
                        </span>
                        <span className={styles.text}>{text}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default StartCards;
